import React, { useId, useMemo } from "react";
import type { AlternateSelf } from "@/lib/infiniteSelves";

/**
 * Deterministic PRNG: Mulberry32
 * Fast 32-bit generator with clean dispersion, seeded per visual.seed.
 */
function mulberry32(a: number) {
  return function () {
    let t = a += 0x6d2b79f5;
    t = Math.imul(t ^ t >>> 15, t | 1);
    t ^= t + Math.imul(t ^ t >>> 7, t | 61);
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
}
export default function SelfArt(props: {
  self: AlternateSelf;
  variant?: "card" | "hero";
  className?: string;
}): JSX.Element {
  const {
    self,
    variant = "card",
    className = ""
  } = props;
  const {
    visual,
    timelineId,
    universe,
    scenarioLabel,
    name
  } = self;
  const isHero = variant === "hero";
  const rawId = useId().replace(/:/g, "_");
  const uid = `self-art-${rawId}`;
  const blurFilterId = `blur-${uid}`;
  const skyGradId = `sky-${uid}`;
  const nebula1Id = `neb1-${uid}`;
  const nebula2Id = `neb2-${uid}`;
  const motifGradId = `motif-grad-${uid}`;

  // Deterministic star field
  const stars = useMemo(() => {
    const starRng = mulberry32(visual.seed + 333);
    const density = self.universe.profile?.visual?.starDensity ?? 0.5;
    const baseCount = isHero ? Math.round(45 + density * 85) : Math.round(25 + density * 55);
    const count = Math.max(15, Math.min(isHero ? 140 : 80, baseCount));
    const result: {
      cx: number;
      cy: number;
      r: number;
      opacity: number;
    }[] = [];
    for (let i = 0; i < count; i++) {
      result.push({
        cx: Math.round(starRng() * 600 * 10) / 10,
        cy: Math.round(starRng() * 340 * 10) / 10,
        r: Math.round((0.4 + starRng() * (isHero ? 1.8 : 1.3)) * 100) / 100,
        opacity: Math.round((0.25 + starRng() * 0.75) * 100) / 100
      });
    }
    return result;
  }, [visual.seed, self.universe.profile?.visual?.starDensity, isHero]);

  // Central motif geometry per visual.motif
  const motifElements = useMemo(() => {
    const motifRng = mulberry32(visual.seed + 777);
    const motif = visual.motif;
    const h1 = visual.hue;
    const h2 = visual.hue2;
    const cx = 300;
    const cy = 170;
    switch (motif) {
      case "spiral":
        {
          // Logarithmic galaxy arms
          const arms = 3;
          const paths: JSX.Element[] = [];
          const armPointsCount = isHero ? 45 : 30;
          for (let arm = 0; arm < arms; arm++) {
            const baseAngle = arm * (2 * Math.PI) / arms;
            let d = "";
            for (let step = 0; step < armPointsCount; step++) {
              const t = step / armPointsCount;
              const r = 12 + t * 145;
              const angle = baseAngle + t * 4.2;
              const px = cx + Math.cos(angle) * r;
              const py = cy + Math.sin(angle) * r * 0.65; // isometric perspective
              d += (step === 0 ? "M" : "L") + ` ${px.toFixed(1)} ${py.toFixed(1)}`;
            }
            paths.push(<path key={`spiral-arm-${arm}`} d={d} fill="none" stroke={`hsl(${h1 + arm * 20} 95% 65% / 0.7)`} strokeWidth={isHero ? 2.2 : 1.6} strokeLinecap="round" />);
          }
          return <g>
            {paths}
            <circle cx={cx} cy={cy} r={isHero ? 14 : 10} fill={`hsl(${h2} 100% 75% / 0.45)`} filter={`url(#${blurFilterId})`} />
            <circle cx={cx} cy={cy} r={isHero ? 6 : 4} fill="#ffffff" />
          </g>;
        }
      case "lattice":
        {
          // Glowing node network of an advanced civilization
          const nodeCount = isHero ? 18 : 12;
          const nodes: {
            x: number;
            y: number;
            r: number;
          }[] = [];
          for (let i = 0; i < nodeCount; i++) {
            nodes.push({
              x: cx + (motifRng() - 0.5) * (isHero ? 340 : 260),
              y: cy + (motifRng() - 0.5) * (isHero ? 190 : 150),
              r: 2 + motifRng() * (isHero ? 3.5 : 2.5)
            });
          }
          const lines: JSX.Element[] = [];
          for (let i = 0; i < nodes.length; i++) {
            for (let j = i + 1; j < nodes.length; j++) {
              const dx = nodes[i].x - nodes[j].x;
              const dy = nodes[i].y - nodes[j].y;
              const dist = Math.hypot(dx, dy);
              if (dist < (isHero ? 120 : 95)) {
                lines.push(<line key={`lattice-line-${i}-${j}`} x1={nodes[i].x.toFixed(1)} y1={nodes[i].y.toFixed(1)} x2={nodes[j].x.toFixed(1)} y2={nodes[j].y.toFixed(1)} stroke={`hsl(${h1} 90% 70% / ${Math.max(0.15, (1 - dist / 120) * 0.6)})`} strokeWidth={1} />);
              }
            }
          }
          return <g>
            {lines}
            {nodes.map((n, idx) => <g key={`lattice-node-${idx}`}>
                <circle cx={n.x} cy={n.y} r={n.r * 2.2} fill={`hsl(${h2} 100% 70% / 0.25)`} />
                <circle cx={n.x} cy={n.y} r={n.r} fill={`hsl(${h1} 100% 80%)`} />
              </g>)}
          </g>;
        }
      case "orbits":
        {
          // Nested luminous ellipses with planetary bodies
          const rings = isHero ? 5 : 4;
          const bodies: JSX.Element[] = [];
          const ringPaths: JSX.Element[] = [];
          for (let i = 0; i < rings; i++) {
            const rx = 40 + i * (isHero ? 34 : 28);
            const ry = 18 + i * (isHero ? 16 : 13);
            const rot = -18 + i * 9;
            ringPaths.push(<ellipse key={`orbit-ring-${i}`} cx={cx} cy={cy} rx={rx} ry={ry} transform={`rotate(${rot} ${cx} ${cy})`} fill="none" stroke={`hsl(${h1 + i * 15} 85% 65% / 0.4)`} strokeWidth={1} strokeDasharray={i % 2 === 0 ? "none" : "5 4"} />);
            // Deterministic body on orbit
            const bodyAngle = motifRng() * Math.PI * 2;
            const rad = rot * Math.PI / 180;
            const lx = Math.cos(bodyAngle) * rx;
            const ly = Math.sin(bodyAngle) * ry;
            const bx = cx + lx * Math.cos(rad) - ly * Math.sin(rad);
            const by = cy + lx * Math.sin(rad) + ly * Math.cos(rad);
            bodies.push(<g key={`orbit-body-${i}`}>
              <circle cx={bx} cy={by} r={3 + motifRng() * 2} fill={`hsl(${h2} 100% 75%)`} />
              <circle cx={bx} cy={by} r={7 + motifRng() * 4} fill={`hsl(${h1} 100% 70% / 0.3)`} />
            </g>);
          }
          return <g>
            {ringPaths}
            <circle cx={cx} cy={cy} r={isHero ? 16 : 12} fill={`url(#${motifGradId})`} />
            <circle cx={cx} cy={cy} r={isHero ? 6 : 4} fill="#ffffff" />
            {bodies}
          </g>;
        }
      case "rift":
        {
          // Jagged luminous fracture splitting the sky
          const segCount = isHero ? 9 : 7;
          let d = `M ${cx - (isHero ? 180 : 130)} ${cy - (isHero ? 95 : 70)}`;
          let currX = cx - (isHero ? 180 : 130);
          let currY = cy - (isHero ? 95 : 70);
          const totalSpanX = isHero ? 360 : 260;
          const dx = totalSpanX / segCount;
          for (let i = 1; i <= segCount; i++) {
            currX += dx;
            currY = cy + (motifRng() - 0.5) * (isHero ? 110 : 80);
            d += ` L ${currX.toFixed(1)} ${currY.toFixed(1)}`;
          }
          return <g>
            <path d={d} fill="none" stroke={`hsl(${h2} 100% 60% / 0.35)`} strokeWidth={isHero ? 12 : 8} strokeLinecap="round" filter={`url(#${blurFilterId})`} />
            <path d={d} fill="none" stroke={`hsl(${h1} 100% 70% / 0.85)`} strokeWidth={isHero ? 3 : 2} strokeLinecap="round" />
            <path d={d} fill="none" stroke="#ffffff" strokeWidth={isHero ? 1.2 : 0.8} strokeLinecap="round" />
          </g>;
        }
      case "bloom":
        {
          // Radial petals/rays emanating outward
          const rays = isHero ? 16 : 12;
          const rayPaths: JSX.Element[] = [];
          for (let i = 0; i < rays; i++) {
            const angle = i * 2 * Math.PI / rays;
            const rInner = 14;
            const rOuter = 55 + motifRng() * (isHero ? 80 : 55);
            const x1 = cx + Math.cos(angle) * rInner;
            const y1 = cy + Math.sin(angle) * rInner;
            const x2 = cx + Math.cos(angle) * rOuter;
            const y2 = cy + Math.sin(angle) * rOuter;
            rayPaths.push(<line key={`bloom-ray-${i}`} x1={x1.toFixed(1)} y1={y1.toFixed(1)} x2={x2.toFixed(1)} y2={y2.toFixed(1)} stroke={`hsl(${h1 + i % 3 * 15} 90% 65% / 0.65)`} strokeWidth={isHero ? 2 : 1.4} strokeLinecap="round" />);
          }
          return <g>
            {rayPaths}
            <circle cx={cx} cy={cy} r={isHero ? 24 : 18} fill={`hsl(${h2} 100% 65% / 0.35)`} filter={`url(#${blurFilterId})`} />
            <circle cx={cx} cy={cy} r={isHero ? 10 : 8} fill={`hsl(${h1} 100% 75%)`} />
            <circle cx={cx} cy={cy} r={3} fill="#ffffff" />
          </g>;
        }
      case "tide":
      default:
        {
          // Layered flowing wave bands across the horizon
          const bands = isHero ? 5 : 4;
          const wavePaths: JSX.Element[] = [];
          for (let i = 0; i < bands; i++) {
            const yOffset = cy - 40 + i * (isHero ? 22 : 18);
            const amp = 14 + motifRng() * 12;
            const d = `M 0 ${yOffset} Q 150 ${yOffset - amp} 300 ${yOffset} T 600 ${yOffset}`;
            wavePaths.push(<path key={`tide-band-${i}`} d={d} fill="none" stroke={`hsl(${h1 + i * 18} 85% 65% / ${0.35 + i * 0.1})`} strokeWidth={isHero ? 2.2 : 1.5} />);
          }
          return <g>{wavePaths}</g>;
        }
    }
  }, [visual.motif, visual.seed, visual.hue, visual.hue2, isHero, blurFilterId, motifGradId]);
  const altDescription = `Cosmic visualizer representation of alternate self ${name} in scenario ${scenarioLabel}. Timeline ${timelineId}, Universe ${universe.code}.`;
  return <div className={`relative w-full aspect-video rounded-lg overflow-hidden border border-border bg-[hsl(240_16%_3%)] shadow-inner select-none ${className}`}>
      <svg viewBox="0 0 600 340" className="w-full h-full block" role="img" aria-label={altDescription} preserveAspectRatio="xMidYMid slice">
        <defs>
          <filter id={blurFilterId} x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation={isHero ? "26" : "20"} />
          </filter>
          <radialGradient id={skyGradId} cx="50%" cy="50%" r="75%">
            <stop offset="0%" stopColor={`hsl(${visual.hue} 45% 12% / 0.85)`} />
            <stop offset="65%" stopColor={`hsl(${visual.hue2} 40% 6% / 0.65)`} />
            <stop offset="100%" stopColor="hsl(240 16% 3%)" />
          </radialGradient>
          <radialGradient id={nebula1Id} cx="35%" cy="40%" r="55%">
            <stop offset="0%" stopColor={`hsl(${visual.hue} 90% 55% / 0.45)`} />
            <stop offset="60%" stopColor={`hsl(${visual.hue} 70% 35% / 0.15)`} />
            <stop offset="100%" stopColor="transparent" />
          </radialGradient>
          <radialGradient id={nebula2Id} cx="70%" cy="65%" r="50%">
            <stop offset="0%" stopColor={`hsl(${visual.hue2} 85% 50% / 0.4)`} />
            <stop offset="65%" stopColor={`hsl(${visual.hue2} 60% 30% / 0.12)`} />
            <stop offset="100%" stopColor="transparent" />
          </radialGradient>
          <radialGradient id={motifGradId} cx="40%" cy="40%" r="60%">
            <stop offset="0%" stopColor={`hsl(${visual.hue} 100% 75%)`} />
            <stop offset="100%" stopColor={`hsl(${visual.hue2} 90% 45%)`} />
          </radialGradient>
        </defs>

        {/* Space Ground */}
        <rect width="600" height="340" fill={`url(#${skyGradId})`} />

        {/* Diffused Nebulae */}
        <rect width="600" height="340" fill={`url(#${nebula1Id})`} filter={`url(#${blurFilterId})`} />
        <rect width="600" height="340" fill={`url(#${nebula2Id})`} filter={`url(#${blurFilterId})`} />

        {/* Star Field */}
        <g>
          {stars.map((s, idx) => <circle key={`star-${idx}`} cx={s.cx} cy={s.cy} r={s.r} fill="#ffffff" opacity={s.opacity} />)}
        </g>

        {/* Central Seeded Motif */}
        {motifElements}

        {/* Subtle Horizon Hairline */}
        <line x1="0" y1="339" x2="600" y2="339" stroke={`hsl(${visual.hue} 100% 50% / 0.4)`} strokeWidth="1" />
      </svg>

      {/* Viewport Meta Badges */}
      <div className="absolute top-2.5 left-2.5 flex items-center gap-2 pointer-events-none">
        <span className="size-2 rounded-full animate-pulse" style={{
        backgroundColor: `hsl(${visual.hue} 100% 60%)`
      }} />
        <span className="text-[10px] font-mono tracking-wider uppercase text-foreground/90 bg-background/80 px-2 py-0.5 rounded border border-border/70 backdrop-blur-sm">
          {timelineId}
        </span>
      </div>
      <div className="absolute top-2.5 right-2.5 pointer-events-none">
        <span className="text-[10px] font-mono text-foreground/80 bg-background/80 px-2 py-0.5 rounded border border-border/70 backdrop-blur-sm">
          {universe.code}
        </span>
      </div>
    </div>;
}